import { useState } from "react";
import { Radar } from "react-chartjs-2";
import { classNames } from "primereact/utils";
import {
  MUSCLE_GROUP_CATEGORY_LABELS,
  MUSCLE_GROUP_CATEGORY_ORDER,
} from "../../../../../common/components/workoutPlanDialog/models/muscleGroups";
import { IWorkoutStatisticsMuscle } from "../models/workoutStatisticsViewModel";
import { formatVolume } from "../utils/statisticsFormat";
import {
  CHART_ACCENT,
  CHART_ACCENT_SOFT,
  CHART_GRID_COLOR,
  CHART_TEXT_COLOR,
} from "../charts/registerCharts";
import "../charts/radarSectorInteraction";
import styles from "../WorkoutStatistics.module.scss";

type Metric = "sets" | "reps" | "volumeKg";

const METRICS: { key: Metric; label: string }[] = [
  { key: "sets", label: "Серии" },
  { key: "reps", label: "Повторения" },
  { key: "volumeKg", label: "Обем" },
];

interface IMuscleDistributionRadarProps {
  breakdown: IWorkoutStatisticsMuscle[];
}

/**
 * Radar over the high-level muscle categories — the headline "which muscles did I
 * train" view. The metric (sets / reps / volume) is toggleable; a working set counts
 * toward every category its exercise targets, so this shows training emphasis, not a
 * 100% share.
 */
export const MuscleDistributionRadar = ({
  breakdown,
}: IMuscleDistributionRadarProps) => {
  const [metric, setMetric] = useState<Metric>("sets");

  const byCategory = new Map(breakdown.map((b) => [b.category, b]));
  const labels = MUSCLE_GROUP_CATEGORY_ORDER.map(
    (category) => MUSCLE_GROUP_CATEGORY_LABELS[category],
  );
  const values = MUSCLE_GROUP_CATEGORY_ORDER.map(
    (category) => byCategory.get(category)?.[metric] ?? 0,
  );
  const metricLabel = METRICS.find((m) => m.key === metric)!.label;
  const formatValue = (value: number) => Math.round(value).toLocaleString("bg-BG");

  const data = {
    labels,
    datasets: [
      {
        label: metricLabel,
        data: values,
        backgroundColor: CHART_ACCENT_SOFT,
        borderColor: CHART_ACCENT,
        borderWidth: 2,
        pointBackgroundColor: CHART_ACCENT,
        pointRadius: 4,
        pointHoverRadius: 7,
        // A finger is much bigger than the 4px point.
        pointHitRadius: 20,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // A tap anywhere on the chart shows the one category it points to, so nobody has
    // to hit a tiny point on a phone (and the 0s in the centre are not listed together).
    interaction: { mode: "radarSector" as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ({ dataIndex }: { dataIndex: number }) =>
            metric === "volumeKg"
              ? `${metricLabel}: ${formatVolume(values[dataIndex])}`
              : `${metricLabel}: ${formatValue(values[dataIndex])}`,
        },
      },
    },
    scales: {
      r: {
        angleLines: { color: CHART_GRID_COLOR },
        grid: { color: CHART_GRID_COLOR },
        pointLabels: {
          color: CHART_TEXT_COLOR,
          font: { size: 12 },
          // The value sits under its category, so it reads without any tap.
          callback: (label: string, index: number) => [label, formatValue(values[index])],
        },
        ticks: { display: false, backdropColor: "transparent" },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className={styles.chart_card}>
      <div className={styles.chart_head}>
        <h3 className={styles.chart_title}>Натоварени мускулни групи</h3>
        <div className={styles.metric_switch}>
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              className={classNames(styles.metric_chip, {
                [styles.metric_chip_active]: metric === m.key,
              })}
              onClick={() => setMetric(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className={classNames(styles.chart_canvas, styles.chart_canvas_radar)}>
        <Radar data={data} options={options} />
      </div>
    </div>
  );
};
